import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileUp, Loader2, ScanLine } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DOC_TYPES = ["passport", "visa", "id", "certification", "contract", "other"];

export default function UploadDialog({ open, onOpenChange, employees, user, onSaved }) {
  const inputRef = useRef(null);
  const isHR = ["admin", "hr_manager"].includes(user.role);
  const [file, setFile] = useState(null);
  const [employeeId, setEmployeeId] = useState(user.employee_id || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docId, setDocId] = useState(null);
  const [review, setReview] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) {
      setFile(null); setDocId(null); setReview(null); setForm({});
      setEmployeeId(isHR ? "" : user.employee_id || "");
    }
  }, [open, isHR, user.employee_id]);

  const upload = async () => {
    if (!file || !employeeId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("employee_id", employeeId);
      const { data } = await api.post("/documents/upload", fd);
      setDocId(data.document.id);
      setReview(data.review);
      setForm({
        doc_type: data.review.doc_type || "other",
        title: data.review.title || "",
        doc_number: data.review.doc_number || "",
        issue_date: data.review.issue_date || "",
        expiry_date: data.review.expiry_date || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/documents/${docId}/confirm`, {
        doc_type: form.doc_type, title: form.title, doc_number: form.doc_number || null,
        issue_date: form.issue_date || null, expiry_date: form.expiry_date || null,
      });
      toast.success(isHR ? "Document saved and verified" : "Document saved — pending HR verification");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">
            {review ? "Review extracted metadata" : "Upload document"}
          </DialogTitle>
        </DialogHeader>

        {!review ? (
          <div className="space-y-4">
            {isHR && (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Employee</Label>
                <Select value={employeeId} onValueChange={setEmployeeId}>
                  <SelectTrigger className="rounded-none mt-1 text-[13px]" data-testid="upload-employee-select">
                    <SelectValue placeholder="Select employee…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none max-h-64">
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id} className="rounded-none text-[13px]">
                        {e.name} — {e.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div
              data-testid="upload-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]); }}
              className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                file ? "border-teal-600 bg-teal-50/40" : "border-border hover:border-slate-400"}`}
            >
              <FileUp className="h-6 w-6 mx-auto text-slate-400" />
              <p className="text-[13px] text-slate-700 mt-2 font-medium">
                {file ? file.name : "Drop a file here or click to browse"}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">PNG, JPEG, WEBP or PDF · max 10MB</p>
              <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
                className="hidden" data-testid="upload-file-input"
                onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
            </div>
            <Button onClick={upload} disabled={!file || !employeeId || uploading} data-testid="upload-submit-btn"
              className="rounded-none w-full bg-teal-700 hover:bg-teal-800">
              {uploading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Running vision OCR…</>)
                : (<><ScanLine className="h-4 w-4" /> Upload & extract</>)}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {review.ocr_error ? (
              <div className="border border-amber-400 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="ocr-error-alert">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5" />
                OCR failed — enter the metadata manually. ({review.ocr_error})
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono px-2 py-1 bg-teal-50 text-teal-800 border border-teal-600/30">
                    OCR confidence {Math.round((review.confidence || 0) * 100)}%
                  </span>
                  <span className="text-[10px] font-mono px-2 py-1 bg-slate-100 text-slate-600">
                    holder {review.holder_name_masked || "—"}
                  </span>
                </div>
                {review.mismatch ? (
                  <div className="border border-red-400 bg-red-50 px-3 py-2 text-[12px] text-red-700" data-testid="holder-mismatch-alert">
                    <AlertTriangle className="h-3.5 w-3.5 inline mr-1.5" />
                    Holder name does not match the selected employee — verify before saving.
                  </div>
                ) : review.holder_match != null && (
                  <div className="border border-teal-600/40 bg-teal-50 px-3 py-2 text-[12px] text-teal-800" data-testid="holder-match-ok">
                    <CheckCircle2 className="h-3.5 w-3.5 inline mr-1.5" />
                    Holder matches the selected employee ({Math.round(review.holder_match * 100)}% token overlap).
                  </div>
                )}
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Type</Label>
                <Select value={form.doc_type} onValueChange={set("doc_type")}>
                  <SelectTrigger className="rounded-none mt-1 text-[13px]" data-testid="review-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {DOC_TYPES.map((t) => <SelectItem key={t} value={t} className="rounded-none text-[13px]">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Title</Label>
                <Input value={form.title} onChange={(e) => set("title")(e.target.value)}
                  className="rounded-none mt-1 text-[13px]" data-testid="review-title-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Document number</Label>
                <Input value={form.doc_number} onChange={(e) => set("doc_number")(e.target.value)}
                  className="rounded-none mt-1 text-[13px] font-mono" data-testid="review-number-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Shown masked — the extracted number is already stored protected. Type a full number only to correct it.</p>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Issue date</Label>
                <Input type="date" value={form.issue_date || ""} onChange={(e) => set("issue_date")(e.target.value)}
                  className="rounded-none mt-1 text-[13px]" data-testid="review-issue-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Expiry date</Label>
                <Input type="date" value={form.expiry_date || ""} onChange={(e) => set("expiry_date")(e.target.value)}
                  className="rounded-none mt-1 text-[13px]" data-testid="review-expiry-input" />
              </div>
            </div>
            <Button onClick={confirm} disabled={saving || !form.title} data-testid="review-confirm-btn"
              className="rounded-none w-full bg-[var(--brand-primary)] hover:bg-slate-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Confirm & save
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
